import React, { useEffect, useState } from "react";
import api, { formatDate, VADE_LABELS, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Building2, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const empty = {
  name: "", tax_number: "", phone: "", email: "", address: "",
  vade_term: "30", vade_farki_pct: 3, currency: "TRY", notes: "",
};

export default function Companies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  async function load() {
    const { data } = await api.get("/companies");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setForm(empty); setEditId(null); setOpen(true); }
  function openEdit(c) {
    setForm({ ...empty, ...c });
    setEditId(c.id);
    setOpen(true);
  }

  async function save() {
    try {
      if (!form.name.trim()) return toast.error("Firma adı zorunlu");
      const payload = { ...form, vade_farki_pct: Number(form.vade_farki_pct) };
      if (editId) {
        await api.put(`/companies/${editId}`, payload);
        toast.success("Firma güncellendi");
      } else {
        await api.post("/companies", payload);
        toast.success("Firma eklendi");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function remove(id) {
    if (!window.confirm("Silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/companies/${id}`);
      toast.success("Firma silindi");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  const filtered = items.filter((c) =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()) || (c.tax_number || "").includes(q)
  );

  return (
    <div className="space-y-6" data-testid="companies-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Cari Yönetimi</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <Building2 className="w-8 h-8 text-amber-400" /> Cari Hesaplar
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Firmalarınızı vade ve vade farkı bilgileriyle birlikte yönetin.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma / vergi no" className="pl-9 bg-[#111723] border-white/10 w-64" data-testid="company-search" />
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="add-company-btn">
                <Plus className="w-4 h-4 mr-1" /> Yeni Firma
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0B111C] border-white/10 max-w-lg" data-testid="company-dialog">
              <DialogHeader>
                <DialogTitle>{editId ? "Firma Düzenle" : "Yeni Firma Ekle"}</DialogTitle>
              </DialogHeader>
              <div className="grid gap-3 py-2">
                <div>
                  <Label>Firma Adı *</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="company-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Vergi No</Label>
                    <Input value={form.tax_number} onChange={(e) => setForm({ ...form, tax_number: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
                  </div>
                  <div>
                    <Label>Telefon</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
                  </div>
                </div>
                <div>
                  <Label>E-posta</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
                </div>
                <div>
                  <Label>Adres</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#111723] border-white/10 mt-1" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Vade</Label>
                    <Select value={form.vade_term} onValueChange={(v) => setForm({ ...form, vade_term: v })}>
                      <SelectTrigger className="bg-[#111723] border-white/10 mt-1" data-testid="vade-term-select"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0B111C] border-white/10">
                        {Object.entries(VADE_LABELS).map(([k, l]) => (
                          <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Vade Farkı % (Aylık)</Label>
                    <Input type="number" step="0.1" value={form.vade_farki_pct} onChange={(e) => setForm({ ...form, vade_farki_pct: e.target.value })} className="bg-[#111723] border-white/10 mt-1" data-testid="vade-farki-input" />
                  </div>
                  <div>
                    <Label>Para Birimi</Label>
                    <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                      <SelectTrigger className="bg-[#111723] border-white/10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#0B111C] border-white/10">
                        <SelectItem value="TRY">₺ TL</SelectItem>
                        <SelectItem value="USD">$ USD</SelectItem>
                        <SelectItem value="EUR">€ EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Notlar</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-[#111723] border-white/10 mt-1" rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)} className="border-white/10">İptal</Button>
                <Button onClick={save} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="save-company-btn">
                  {editId ? "Güncelle" : "Kaydet"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="card-elevated rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02]">
              <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
                <th className="py-3 px-5">Firma</th>
                <th className="py-3 px-5">Vergi No</th>
                <th className="py-3 px-5">İletişim</th>
                <th className="py-3 px-5">Vade</th>
                <th className="py-3 px-5">Vade Farkı %</th>
                <th className="py-3 px-5">Para</th>
                <th className="py-3 px-5">Eklendi</th>
                <th className="py-3 px-5 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-10 text-center text-slate-500">Firma bulunamadı</td></tr>
              )}
              {filtered.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`company-row-${c.id}`}>
                  <td className="py-3 px-5 font-medium">{c.name}</td>
                  <td className="py-3 px-5 mono text-slate-400">{c.tax_number || "-"}</td>
                  <td className="py-3 px-5 text-slate-300">
                    <div className="text-xs">{c.phone}</div>
                    <div className="text-xs text-slate-500">{c.email}</div>
                  </td>
                  <td className="py-3 px-5">
                    <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/25">{VADE_LABELS[c.vade_term]}</Badge>
                  </td>
                  <td className="py-3 px-5 tabular font-semibold text-amber-300">%{c.vade_farki_pct}</td>
                  <td className="py-3 px-5 mono">{c.currency}</td>
                  <td className="py-3 px-5 text-slate-400">{formatDate(c.created_at)}</td>
                  <td className="py-3 px-5 text-right">
                    <Link to={`/companies/${c.id}/statement`}>
                      <Button size="sm" variant="ghost" className="text-amber-300 hover:bg-amber-500/10" data-testid={`statement-${c.id}`}>
                        <FileText className="w-4 h-4 mr-1" /> Ekstre
                      </Button>
                    </Link>
                    <Button size="sm" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(c.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`delete-company-${c.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
